import { createSlice } from '@reduxjs/toolkit';

import { dispatch } from '../index';
import {
    getCourses,
    getCoursesAnalytics as getCoursesAnalyticsAPI,
    checkExistCourse as checkExistCourseAPI,
    createCourse as createCourseAPI,
    saveAsDraftCourse as saveAsDraftCourseAPI,
    editCourse as editCourseAPI,
    duplicateCourse as duplicateCourseAPI,
    deleteDraftCourse as deleteDraftCourseAPI,
    deactivateCourse as deactivateCourseAPI,
	activateCourse as activateCourseAPI,
    getCourse
} from 'api/courses';
import { openSnackbar } from './snackbar';
import { TABLE_CONST } from 'constants';

const defaultParams = {
    filter: {
        search: '',
        status: 'All'
    },
    pageNo: 1,
    perPage: TABLE_CONST.DEFAULT_PER_PAGE,
    sort: {
        sortBy: 'name',
        sortDirection: 'ASC'
    }
};

const initialState = {
    courses: [],
    count: 0,
    params: defaultParams,
    isFetching: true
};

const coursesSlice = createSlice({
    name: 'courses',
    initialState,
    reducers: {
        setCourses(state, action) {
            state.courses = action.payload.courses;
            state.count = action.payload.count;
        },
        setIsFetchingCourses(state, action) {
            state.isFetching = action.payload;
        },
        setParams(state, action) {
            const { params, isResetParams = false } = action.payload;
            state.params = isResetParams ? defaultParams : params;
        }
    }
});

export const { setIsFetchingCourses, setParams } = coursesSlice.actions;

export default coursesSlice.reducer;

export const fetchCourses = async ({ pageNo, perPage, filter, sort } = {}) => {
    try {
        const data = {
            pageNo,
            perPage,
            search: filter?.search || '',
            status: filter?.status || '',
            sortBy: sort?.sortBy || 'name',
            sortDirection: sort?.sortDirection || 'ASC'
        };

        const result = await getCourses(data);

        const { list = [], total = 0 } = result.data;

        dispatch(
            coursesSlice.actions.setCourses({
                courses: list,
                count: total
            })
        );
    } catch (err) {
        console.error('Error fetching courses:', err);
        dispatch(
            coursesSlice.actions.setCourses({
                courses: [],
                count: 0
            })
        );
    }
};

export const getCoursesAnalytics = async () => {
    try {
        const result = await getCoursesAnalyticsAPI();
        const { data: analytics = {} } = result;
        return analytics;
    } catch (err) {
        console.error('Error in getCoursesAnalytics:', err);
        return {};
    }
};

export const checkExistCourse = async (data) => {
    try {
        const result = await checkExistCourseAPI(data);
        return result;
    } catch (err) {
        console.error('Error in createCourse:', err);
        return {
            success: false,
            message: err?.message || 'Error in Creating Course'
        };
    }
};

export const saveAsDraftCourse = async (data, showMessage = false) => {
    try {
        if (showMessage) {
            dispatch(
                openSnackbar({
                    open: true,
                    message: 'Saving As Draft ...',
                    variant: 'progress'
                })
            );
        }
        const result = await saveAsDraftCourseAPI(data);
        if (showMessage) {
            if (result.success) {
                dispatch(
                    openSnackbar({
                        open: true,
                        title: 'Save As Draft',
                        message: 'Course Draft information save successfully.',
                        variant: 'alert',
                        alert: {
                            color: 'success'
                        }
                    })
                );
            } else {
                dispatch(
                    openSnackbar({
                        open: true,
                        title: 'Save As Draft',
                        message: result?.message || 'Error in Saving Draft Course',
                        variant: 'alert',
                        alert: {
                            color: 'error'
                        }
                    })
                );
            }
        }
        return result;
    } catch (err) {
        console.error('Error in save draft course:', err);

        if (showMessage) {
            dispatch(
                openSnackbar({
                    open: true,
                    title: 'Save As Draft',
                    message: err?.message || 'Error in Checking Course',
                    variant: 'alert',
                    alert: {
                        color: 'error'
                    }
                })
            );
        }

        return {
            success: false,
            message: err?.message || 'Error in Checking Course'
        };
    }
};

export const createCourse = async (data) => {
    try {
        dispatch(
            openSnackbar({
                open: true,
                message: 'Saving Course ...',
                variant: 'progress'
            })
        );
        const result = await createCourseAPI(data);
        if (result.success) {
            dispatch(
                openSnackbar({
                    open: true,
                    title: 'Create Course',
                    message: `Course ${data?.courseInfo?.name || ''} added successfully.`,
                    variant: 'alert',
                    alert: {
                        color: 'success'
                    }
                })
            );
        } else {
            dispatch(
                openSnackbar({
                    open: true,
                    title: 'Create Course',
                    message: result?.message || 'Error in Saving Course',
                    variant: 'alert',
                    alert: {
                        color: 'error'
                    }
                })
            );
        }
        return result;
    } catch (err) {
        console.error('Error in createCourse:', err);

        dispatch(
            openSnackbar({
                open: true,
                title: 'Create Course',
                message: err?.message || 'Error in Saving Course',
                variant: 'alert',
                alert: {
                    color: 'error'
                }
            })
        );

        return {
            success: false,
            message: err?.message || 'Error in Saving Course'
        };
    }
};

export const fetchCourse = async (id, { includeVersionHistory = false } = {}) => {
    try {
        const result = await getCourse(id, {
            includeVersionHistory
        });
        return result;
    } catch (err) {
        console.error('Error fetching course:', err);
    }
};

export const editCourse = async (id, data, mode = 'edit') => {
    try {
        dispatch(
            openSnackbar({
                open: true,
                message: 'Saving Course ...',
                variant: 'progress'
            })
        );
        const result = await editCourseAPI(id, data);
        if (result.success) {
            dispatch(
                openSnackbar({
                    open: true,
                    title: mode === 'Edit' ? 'Edit Course' : 'Create Course',
                    message: `Course ${data?.courseInfo?.name || ''} ${mode === 'edit' ? 'updated' : 'created'} successfully.`,
                    variant: 'alert',
                    alert: {
                        color: 'success'
                    }
                })
            );
        } else {
            dispatch(
                openSnackbar({
                    open: true,
                    title: mode === 'Edit' ? 'Edit Course' : 'Create Course',
                    message: result?.message || `Error in ${mode === 'edit' ? 'updating' : 'creating'} Course`,
                    variant: 'alert',
                    alert: {
                        color: 'error'
                    }
                })
            );
        }
        return result;
    } catch (err) {
        console.error('Error in editCourse:', err);

        dispatch(
            openSnackbar({
                open: true,
                title: 'Edit Course',
                message: err?.message || 'Error in Updating Course',
                variant: 'alert',
                alert: {
                    color: 'error'
                }
            })
        );

        return {
            success: false,
            message: err?.message || 'Error in Updating Course'
        };
    }
};

export const duplicateCourse = async (id) => {
    try {
        const result = await duplicateCourseAPI(id);
        return result;
    } catch (err) {
        console.error('Error in duplicateCourse:', err);
        return {
            success: false,
            message: err?.message || 'Error in Duplicating Course'
        };
    }
};

export const deleteDraftCourse = async (id) => {
    try {
        return await deleteDraftCourseAPI(id);
    } catch (err) {
        console.error('Error deleting draft course:', err);
        return {
            success: false,
            message: err?.message || 'Unable to delete draft course'
        };
    }
};

export const deactivateCourse = async (id) => {
    try {
        return await deactivateCourseAPI(id);
    } catch (err) {
        console.error('Error deactivating course:', err);
        return {
            success: false,
            message: err?.message || 'Unable to deactivate course'
        };
    }
};

export const activateCourse = async (id) => {
    try {
        return await activateCourseAPI(id);
    } catch (err) {
        console.error('Error activating course:', err);
        return {
            success: false,
            message: err?.message || 'Unable to activate course'
        };
    }
};
